import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { TbAlertTriangle, TbArrowDown, TbChevronDown } from "react-icons/tb";
import { PvsTips } from "./PvsTips";
import { TrayScene } from "./TrayScene";
import { ReviewTabScene } from "./ReviewTabScene";
import { SYNC_GUIDE_MEDIA, type GuidePhaseKey } from "./syncGuideMedia";
import "./syncGuide.scss";

export type GuideMethod = "browser" | "agent";
export type GuideJumpTarget = "devices" | "folders";

const PHASES: Record<GuideMethod, GuidePhaseKey[]> = {
    agent: ["prepare", "install", "connect", "daily"],
    browser: ["prepare", "connectBrowser", "dailyBrowser"],
};

// Bullet points per phase, in reading order. "prepare" and the agent's "daily" carry their own visuals.
const ITEMS: Record<GuidePhaseKey, readonly string[]> = {
    prepare: ["invoices"],
    install: ["download", "smartscreen", "autostart"],
    connect: ["code", "setupTab", "folders", "sharedPc", "test"],
    daily: ["extras"],
    connectBrowser: ["browser", "connect", "allow", "test"],
    dailyBrowser: ["keepOpen", "export", "confirm", "review"],
};

// Phases whose action lives further down the page — a jump link saves hunting for the button.
const JUMPS: Partial<Record<GuidePhaseKey, GuideJumpTarget>> = {
    install: "devices",
    connect: "devices",
    connectBrowser: "folders",
};

const GLOSSARY = ["gdt", "pad", "exportFolder", "tray", "code"] as const;

// Support mailbox for practices that can't set up the GDT device themselves — the fixed subject lets support
// spot these requests.
const GDT_HELP_EMAIL = "support@medinvoice.io";
const GDT_HELP_SUBJECT = "GDT-Einrichtung Desktop-App";

function GdtHelpBanner() {
    const { t } = useTranslation();
    const href = `mailto:${GDT_HELP_EMAIL}?subject=${encodeURIComponent(GDT_HELP_SUBJECT)}`;
    return (
        <div className="sync-guide__help" role="note">
            <TbAlertTriangle aria-hidden="true" />
            <p>
                {t("syncGuide.gdtHelp.question")}{" "}
                {t("syncGuide.gdtHelp.contact")} <a href={href}>{GDT_HELP_EMAIL}</a>{" "}
                {t("syncGuide.gdtHelp.subject", { subject: GDT_HELP_SUBJECT })}
            </p>
        </div>
    );
}

function reducedMotion(): boolean {
    try {
        return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch {
        return false;
    }
}

function PhaseClip({ phase, title }: { phase: GuidePhaseKey; title: string }) {
    const { t } = useTranslation();
    const clip = SYNC_GUIDE_MEDIA[phase];
    if (!clip) return null;
    return (
        <video
            className="sync-guide__clip"
            src={clip.src}
            poster={clip.poster}
            aria-label={t("syncGuide.clipLabel", { title })}
            autoPlay={!reducedMotion()}
            muted
            loop
            playsInline
            controls
            preload="metadata"
        />
    );
}

/** The step-by-step setup for one sync method, written for practice staff who have never connected a
 *  device to their practice software themselves. Phases, not a flat list: prepare the practice software →
 *  (install →) connect → everyday use, so the one-off setup and the daily routine read separately.
 *  Shown inside the Sync page's how-it-works popup (SyncHowItWorks.tsx). */
export function SyncGuide({ method, onJump }: { method: GuideMethod; onJump?: (target: GuideJumpTarget) => void }) {
    const { t } = useTranslation();

    return (
        <div className="sync-guide">
            <p className="sync-guide__analogy">{t("syncGuide.analogy")}</p>

            <ol className="sync-guide__phases">
                {PHASES[method].map((phase) => {
                    const title = t(`syncGuide.phases.${phase}.title`);
                    const jump = JUMPS[phase];
                    let visual: ReactNode = null;
                    if (phase === "prepare") visual = <PvsTips />;
                    if (phase === "daily") {
                        visual = (
                            <>
                                <div className="sync-guide__scene">
                                    <h4 className="sync-guide__scene-title">{t("syncGuide.tray.title")}</h4>
                                    <TrayScene />
                                </div>
                                <div className="sync-guide__scene">
                                    <h4 className="sync-guide__scene-title">{t("syncGuide.reviewTab.title")}</h4>
                                    <ReviewTabScene />
                                </div>
                            </>
                        );
                    }

                    return (
                        <li key={phase} className="sync-guide__phase">
                            <h3 className="sync-guide__phase-title">{title}</h3>
                            <p className="sync-guide__phase-lead">{t(`syncGuide.phases.${phase}.lead`)}</p>
                            {visual}
                            <ul className="sync-guide__items">
                                {ITEMS[phase].map((item) => (
                                    <li key={item}>{t(`syncGuide.phases.${phase}.items.${item}`)}</li>
                                ))}
                            </ul>
                            <PhaseClip phase={phase} title={title} />
                            {method === "agent" && phase === "prepare" && <GdtHelpBanner />}
                            {jump && onJump && (
                                <button type="button" className="sync-guide__jump" onClick={() => onJump(jump)}>
                                    {t(`syncGuide.jump.${jump}`)} <TbArrowDown aria-hidden="true" />
                                </button>
                            )}
                        </li>
                    );
                })}
            </ol>

            <details className="sync-guide__glossary">
                <summary>
                    <TbChevronDown aria-hidden="true" />
                    {t("syncGuide.glossary.title")}
                </summary>
                <dl>
                    {GLOSSARY.map((g) => (
                        <div key={g} className="sync-guide__term">
                            <dt>{t(`syncGuide.glossary.${g}.term`)}</dt>
                            <dd>{t(`syncGuide.glossary.${g}.text`)}</dd>
                        </div>
                    ))}
                </dl>
            </details>
        </div>
    );
}
